import { gql } from "@apollo/client";

// NOTE: The legacy word-based auth mutations (requestAuth / verifyAuth /
// requestAuthViaSMS / verifyAuthViaSMS) were removed 2026-06-03. Login now uses the
// single 4-digit numeric flow (sendSmsCode/verifySmsCode + sendEmailCode/verifyEmailCode,
// defined in hooks/useAuthVerification.ts). See vault/how-to/upgraded-authentication-module.md.

// Client update mutation
export const UPDATE_CLIENT = gql`
  mutation UpdateClient($id: ID!, $input: UpdateClientInput!) {
    updateClient(id: $id, input: $input) {
      id
      fName
      lName
      email
      phoneNumber
    }
  }
`;

// Client queries for authentication context
export const GET_CLIENT = gql`
  query GetClientByEmail($email: String!) {
    clientByEmail(email: $email) {
      id
      email
      fName
      lName
      phoneNumber
      role
      isVerifiedSeller
      businessName
      businessRegistrationNumber
      profilePhoto
      shippingAddresses {
        name
        phone
        address {
          street
          city
          state
          postcode
          country
        }
        isDefault
        instructions
      }
      billingAddresses {
        name
        phone
        address {
          street
          city
          state
          postcode
          country
        }
        isDefault
        instructions
      }
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
      }
      permissions
      encryptedSignatureIpfsUrl
      createdAt
      updatedAt
      tenantId
    }
  }
`;

export const GET_CLIENT_BY_PHONE = gql`
  query GetClientByPhone($phoneNumber: String!) {
    clientByPhone(phoneNumber: $phoneNumber) {
      id
      email
      fName
      lName
      phoneNumber
      role
      isVerifiedSeller
      businessName
      businessRegistrationNumber
      profilePhoto
      shippingAddresses {
        name
        phone
        address {
          street
          city
          state
          postcode
          country
        }
        isDefault
        instructions
      }
      billingAddresses {
        name
        phone
        address {
          street
          city
          state
          postcode
          country
        }
        isDefault
        instructions
      }
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
      }
      permissions
      encryptedSignatureIpfsUrl
      createdAt
      updatedAt
      tenantId
    }
  }
`;

// BEST PRACTICE: Query client by ID for most efficient lookup
export const GET_CLIENT_BY_ID = gql`
  query GetClientById($id: ID!) {
    client(id: $id) {
      id
      email
      fName
      lName
      phoneNumber
      role
      isVerifiedSeller
      businessName
      businessRegistrationNumber
      profilePhoto
      shippingAddresses {
        name
        phone
        address {
          street
          city
          state
          postcode
          country
        }
        isDefault
        instructions
      }
      billingAddresses {
        name
        phone
        address {
          street
          city
          state
          postcode
          country
        }
        isDefault
        instructions
      }
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
      }
      permissions
      encryptedSignatureIpfsUrl
      createdAt
      updatedAt
      tenantId
    }
  }
`;

// Self-service: current user reads their own client record. No ADMIN gate on
// the backend resolver — safe to call from AuthContext on every session.
// Use this instead of GET_CLIENT_BY_ID for the logged-in user's own profile.
export const GET_MY_CLIENT = gql`
  query GetMyClient {
    myClient {
      id
      email
      fName
      lName
      phoneNumber
      username
      passwordChangedAt
      hasUsernamePassword
      role
      isVerifiedSeller
      businessName
      businessRegistrationNumber
      profilePhoto
      shippingAddresses {
        name
        phone
        address {
          street
          city
          state
          postcode
          country
        }
        isDefault
        instructions
      }
      billingAddresses {
        name
        phone
        address {
          street
          city
          state
          postcode
          country
        }
        isDefault
        instructions
      }
      paymentReceivingDetails {
        acceptedMethods
        bankAccount {
          accountName
          bsb
          accountNumber
          bankName
          swiftCode
        }
        cryptoWallets {
          walletAddress
          network
          memo
        }
        stripeConnect {
          stripeAccountId
          accountVerified
          verifiedAt
        }
        paypalEmail
        isVerified
      }
      permissions
      preferences {
        quickLinks {
          order
          hidden
        }
      }
      encryptedSignatureIpfsUrl
      createdAt
      updatedAt
      tenantId
    }
  }
`;

// New mutations for email/phone changes that use client ID
export const REQUEST_EMAIL_CHANGE = gql`
  mutation RequestEmailChange($input: RequestEmailChangeInput!) {
    requestEmailChange(input: $input)
  }
`;

export const VERIFY_EMAIL_CHANGE = gql`
  mutation VerifyEmailChange($input: VerifyEmailChangeInput!) {
    verifyEmailChange(input: $input)
  }
`;

export const REQUEST_PHONE_CHANGE = gql`
  mutation RequestPhoneChange($input: RequestPhoneChangeInput!) {
    requestPhoneChange(input: $input)
  }
`;

export const VERIFY_PHONE_CHANGE = gql`
  mutation VerifyPhoneChange($input: VerifyPhoneChangeInput!) {
    verifyPhoneChange(input: $input)
  }
`;

// ==========================================
// USERNAME/PASSWORD AUTHENTICATION
// ==========================================

export const SIGNUP_WITH_USERNAME = gql`
  mutation SignupWithUsername($input: SignupWithUsernameInput!) {
    signupWithUsername(input: $input) {
      token
      client {
        id
        username
        fName
        lName
        email
        phoneNumber
        permissions
      }
    }
  }
`;

/**
 * Funnel self-signup after an email OTP has been verified. Pairs with
 * UnifiedLoginModal.onAccountNotFound — when the verify step returns LOOKUP_FAILED,
 * the funnel collects a name (+ optional phone) and calls this to create the account
 * and return a session token. Passwordless unless `password` is supplied.
 */
export const REGISTER_WITH_VERIFIED_EMAIL = gql`
  mutation RegisterWithVerifiedEmail($input: RegisterWithVerifiedEmailInput!) {
    registerWithVerifiedEmail(input: $input) {
      token
      accountStatus
      client {
        id
        fName
        lName
        email
        phoneNumber
        permissions
      }
    }
  }
`;

/**
 * Phone sibling of REGISTER_WITH_VERIFIED_EMAIL (2026-08-11) — same funnel
 * self-signup pattern, for a tenant whose UnifiedLoginModal verified a phone
 * number instead of an email. See registerWithVerifiedEmail's own doc above.
 */
export const REGISTER_WITH_VERIFIED_PHONE = gql`
  mutation RegisterWithVerifiedPhone($input: RegisterWithVerifiedPhoneInput!) {
    registerWithVerifiedPhone(input: $input) {
      token
      accountStatus
      client {
        id
        fName
        lName
        email
        phoneNumber
        permissions
      }
    }
  }
`;

export const LOGIN_WITH_USERNAME = gql`
  mutation LoginWithUsername($input: LoginWithUsernameInput!) {
    loginWithUsername(input: $input) {
      token
      client {
        id
        username
        fName
        lName
        email
        phoneNumber
        permissions
      }
    }
  }
`;

export const CHECK_USERNAME_AVAILABLE = gql`
  query CheckUsernameAvailable($username: String!) {
    checkUsernameAvailable(username: $username)
  }
`;

export const SET_PASSWORD = gql`
  mutation SetPassword($input: SetPasswordInput!) {
    setPassword(input: $input)
  }
`;

export const CHANGE_PASSWORD = gql`
  mutation ChangePassword($input: ChangePasswordInput!) {
    changePassword(input: $input)
  }
`;

// Change your password AND sign out every other device (2026-09-28). Returns a fresh token for
// THIS device — adopt it (the old one is refused once sessionsValidAfter moves).
export const CHANGE_MY_PASSWORD = gql`
  mutation ChangeMyPassword($input: ChangePasswordInput!) {
    changeMyPassword(input: $input) {
      token
      signedOutAt
    }
  }
`;

// Sign out everywhere (2026-09-28): every session issued before now stops working. With
// keepThisDevice (default true) the server returns a fresh token for this device — adopt it.
export const SIGN_OUT_EVERYWHERE = gql`
  mutation SignOutEverywhere($keepThisDevice: Boolean) {
    signOutEverywhere(keepThisDevice: $keepThisDevice) {
      token
      signedOutAt
    }
  }
`;

// Where you're signed in (2026-09-28, module 1.5.0) — the caller's OWN active sign-ins,
// most recently active first; `isCurrent` marks this device.
export const MY_SESSIONS = gql`
  query MySessions {
    mySessions {
      id
      createdAt
      lastSeenAt
      deviceLabel
      deviceType
      ipAddress
      country
      amr
      aal
      isCurrent
    }
  }
`;

// Sign out ONE of your own sign-ins. `wasThisDevice` → this device is now signed out too.
export const REVOKE_MY_SESSION = gql`
  mutation RevokeMySession($id: String!) {
    revokeMySession(id: $id) {
      id
      revokedAt
      wasThisDevice
    }
  }
`;

// ==========================================
// PASSWORD RESET
// ==========================================

export const REQUEST_PASSWORD_RESET = gql`
  mutation RequestPasswordReset($input: RequestPasswordResetInput!) {
    requestPasswordReset(input: $input)
  }
`;

export const VERIFY_PASSWORD_RESET_CODE = gql`
  mutation VerifyPasswordResetCode($input: VerifyPasswordResetCodeInput!) {
    verifyPasswordResetCode(input: $input)
  }
`;

export const RESET_PASSWORD = gql`
  mutation ResetPassword($input: ResetPasswordInput!) {
    resetPassword(input: $input)
  }
`;

// ==========================================
// ADMIN PASSWORD RESET
// ==========================================

export const ADMIN_RESET_PASSWORD = gql`
  mutation AdminResetPassword($input: AdminResetPasswordInput!) {
    adminResetPassword(input: $input)
  }
`;

// ==========================================
// TENANT AUTH CONFIG
// ==========================================

// Public query - no auth required - for login modal to get auth config
export const GET_TENANT_AUTH_CONFIG = gql`
  query GetTenantAuthConfig {
    tenantAuthConfig {
      phoneSignInMode emailLinkFlow emailSignInMode allowEmailLinkSignup
      passkeySetupPrompt passwordSetupPrompt collectProfileAfterSignIn profilePromptAfterLogins profilePromptRepeatDays
      profileFields { email fName lName phoneNumber }
      enablePhoneAuth
      enableEmailAuth
      enableUsernamePassword
      enablePasskeyAuth
      defaultAuthMethod
      discreteAlternativeAuth
    }
  }
`;

// Tenant-driven copy for the NoAccountFoundCard (and any other auth screens that
// need the tenant's display name + support email). Reads currentTenant — the
// same source Postmark uses for the From address. Zero hard-coded strings.
export const GET_TENANT_CONTACT_FOR_AUTH = gql`
  query GetTenantContactForAuth {
    currentTenant {
      id
      name
      emailConfig {
        fromEmail
        replyToEmail
      }
    }
  }
`;

// ==========================================
// PASSKEYS (WebAuthn)
// ==========================================

// List the current user's registered passkeys (security page).
export const MY_PASSKEYS = gql`
  query MyPasskeys {
    myPasskeys {
      id
      name
      transports
      deviceType
      backedUp
      lastUsedAt
      createdAt
      rpId
      host
    }
  }
`;

// Step 1 of enrolling a passkey — returns options JSON for startRegistration().
export const GENERATE_PASSKEY_REGISTRATION_OPTIONS = gql`
  mutation GeneratePasskeyRegistrationOptions {
    generatePasskeyRegistrationOptions
  }
`;

// Step 2 of enrolling a passkey — verifies + persists the credential.
export const VERIFY_PASSKEY_REGISTRATION = gql`
  mutation VerifyPasskeyRegistration($input: VerifyPasskeyRegistrationInput!) {
    verifyPasskeyRegistration(input: $input) {
      id
      name
      createdAt
    }
  }
`;

// Step 1 of passkey login — returns options JSON for startAuthentication(). Public.
export const GENERATE_PASSKEY_AUTHENTICATION_OPTIONS = gql`
  mutation GeneratePasskeyAuthenticationOptions {
    generatePasskeyAuthenticationOptions
  }
`;

// Step 2 of passkey login — verifies the assertion, returns a JWT (AuthResponse).
export const VERIFY_PASSKEY_AUTHENTICATION = gql`
  mutation VerifyPasskeyAuthentication($input: VerifyPasskeyAuthenticationInput!) {
    verifyPasskeyAuthentication(input: $input) {
      token
      accountStatus
      client {
        id
        fName
        lName
      }
    }
  }
`;

export const RENAME_PASSKEY = gql`
  mutation RenamePasskey($input: RenamePasskeyInput!) {
    renamePasskey(input: $input) {
      id
      name
    }
  }
`;

export const DELETE_PASSKEY = gql`
  mutation DeletePasskey($id: ID!) {
    deletePasskey(id: $id)
  }
`;

// ==========================================
// LOGIN HISTORY
// ==========================================

// A user's own login activity (self-query is allowed by the backend resolver).
export const GET_LOGIN_ACTIVITIES = gql`
  query LoginActivities($clientId: ID!, $limit: Int, $offset: Int) {
    loginActivities(clientId: $clientId, limit: $limit, offset: $offset) {
      id
      action
      method
      ipAddress
      userAgent
      details
      timestamp
    }
  }
`;

// ==========================================
// ONBOARDING (requires ONBOARDING permission)
// ==========================================

// Onboard a person by email/phone (+ optional username/password). Looks up existing
// accounts first to avoid duplicates, then delivers credentials.
export const ONBOARD_CLIENT = gql`
  mutation OnboardClient($input: OnboardClientInput!) {
    onboardClient(input: $input) {
      success
      created
      matchedExisting
      clientId
      username
      temporaryPassword
      deliveredVia
    }
  }
`;

export const RESET_CLIENT_CREDENTIALS = gql`
  mutation ResetClientCredentials($clientId: ID!, $sendVia: String) {
    resetClientCredentials(clientId: $clientId, sendVia: $sendVia) {
      success
      clientId
      username
      temporaryPassword
      deliveredVia
    }
  }
`;

// Clusters of accounts sharing an email/phone — likely duplicates (account fragmentation).
export const POTENTIAL_DUPLICATE_ACCOUNTS = gql`
  query PotentialDuplicateAccounts {
    potentialDuplicateAccounts {
      identifierType
      identifier
      accounts {
        id
        fName
        lName
        email
        phoneNumber
        username
        hasPassword
        lastLoginAt
        createdAt
      }
    }
  }
`;

// Recently-onboarded accounts + the state each is at (for the /onboarding list).
export const ONBOARDED_CLIENTS = gql`
  query OnboardedClients {
    onboardedClients {
      id
      fName
      lName
      email
      phoneNumber
      username
      hasPassword
      lastLoginAt
      lastLoginMethod
      createdAt
      state
    }
  }
`;

// Merge duplicate accounts into one (requires MERGE_CLIENTS permission on the backend).
export const MERGE_CLIENT_ACCOUNTS = gql`
  mutation MergeClientAccounts($keepClientId: ID!, $mergeClientIds: [ID!]!) {
    mergeClientAccounts(keepClientId: $keepClientId, mergeClientIds: $mergeClientIds) {
      id
    }
  }
`;
export const REQUEST_EMAIL_LOGIN_LINK = gql`
  mutation RequestEmailLoginLink($input: RequestEmailLoginLinkInput!) {
    requestEmailLoginLink(input: $input) { accepted retryAfterSeconds browserToken browserLabel codeAvailable }
  }
`;
export const AUTHENTICATE_EMAIL_LOGIN_LINK = gql`
  mutation AuthenticateEmailLoginLink($token: String!, $next: String) {
    authenticateEmailLoginLink(token: $token, next: $next) {
      auth { token accountStatus }
      proof profileFields { email fName lName phoneNumber } missingFields returnPath flow
    }
  }
`;
export const COMPLETE_EMAIL_LINK_SIGNUP = gql`
  mutation CompleteEmailLinkSignup($input: CompleteEmailLinkSignupInput!) {
    completeEmailLinkSignup(input: $input) {
      auth { token accountStatus }
      proof profileFields { email fName lName phoneNumber } missingFields returnPath flow
    }
  }
`;

export const REVOKE_EMAIL_LOGIN_LINKS = gql`
  mutation RevokeMyEmailLoginLinks { revokeEmailLoginLinks }
`;

export const REQUEST_EMAIL_SIGNUP_PHONE_CODE = gql`
  mutation RequestEmailSignupPhoneCode($proof: String!, $phoneNumber: String!) {
    requestEmailSignupPhoneCode(proof: $proof, phoneNumber: $phoneNumber) { accepted retryAfterSeconds codeAvailable }
  }
`;

export const AUTHENTICATE_EMAIL_LOGIN_CODE = gql`
  mutation AuthenticateEmailLoginCode($browserToken: String!, $code: String!) {
    authenticateEmailLoginCode(browserToken: $browserToken, code: $code) {
      auth { token accountStatus client { id fName lName email phoneNumber permissions } }
      proof profileFields { email fName lName phoneNumber } missingFields returnPath flow
    }
  }
`;
export const GET_AUTH_PROFILE_STATUS = gql`
  query GetAuthProfileStatus { authProfileStatus { requiredFields suggestedFields suggestedCredentials } }
`;
export const DEFER_AUTH_PROFILE = gql`
  mutation DeferAuthProfile { deferAuthProfile }
`;

export const POLL_EMAIL_LOGIN_BROWSER = gql`
  mutation PollEmailLoginBrowser($token: String!) {
    pollEmailLoginBrowser(token: $token) { status auth { token accountStatus } returnPath }
  }
`;

export const EMAIL_LOGIN_BROWSER_APPROVAL = gql`
  mutation EmailLoginBrowserApproval($token: String!) { emailLoginBrowserApproval(token: $token) }
`;

export const APPROVE_EMAIL_LOGIN_BROWSER = gql`
  mutation ApproveEmailLoginBrowser($token: String!, $label: String!) { approveEmailLoginBrowser(token: $token, label: $label) }
`;

export const RESUME_EMAIL_LINK_SIGNUP = gql`
  mutation ResumeEmailLinkSignup($proof: String!) {
    resumeEmailLinkSignup(proof: $proof) {
      auth { token accountStatus }
      proof profileFields { email fName lName phoneNumber } missingFields returnPath flow
    }
  }
`;

export const COMPLETE_AUTH_PROFILE = gql`
  mutation CompleteAuthProfile($input: AuthProfileInput!) { completeAuthProfile(input: $input) { id fName lName email phoneNumber } }
`;

// SMS links are existing-account proof. Module invitations remain separate.
export const REQUEST_SMS_LOGIN_LINK = gql`
  mutation RequestSmsLoginLink($input: RequestSmsLoginLinkInput!) {
    requestSmsLoginLink(input: $input) { accepted retryAfterSeconds browserToken browserLabel codeAvailable }
  }
`;
export const AUTHENTICATE_SMS_LOGIN_LINK = gql`
  mutation AuthenticateSmsLoginLink($token: String!) {
    authenticateSmsLoginLink(token: $token) { status returnPath auth { token client { id } } }
  }
`;
export const AUTHENTICATE_SMS_LOGIN_CODE = gql`
  mutation AuthenticateSmsLoginCode($browserToken: String!, $code: String!) {
    authenticateSmsLoginCode(browserToken: $browserToken, code: $code) { status returnPath auth { token client { id } } }
  }
`;
export const POLL_SMS_LOGIN_BROWSER = gql`
  mutation PollSmsLoginBrowser($token: String!) {
    pollSmsLoginBrowser(token: $token) { status returnPath auth { token client { id } } }
  }
`;
export const SMS_LOGIN_BROWSER_APPROVAL = gql`
  mutation SmsLoginBrowserApproval($token: String!) { smsLoginBrowserApproval(token: $token) }
`;
export const APPROVE_SMS_LOGIN_BROWSER = gql`
  mutation ApproveSmsLoginBrowser($token: String!, $label: String!) { approveSmsLoginBrowser(token: $token, label: $label) }
`;

// ==========================================
// TWO-STEP VERIFICATION / STEP-UP (2026-09-26)
// Contract: the session's assurance lives in the JWT (`aal`, `amr`, `mfaAt`). Every step-up
// mutation re-issues the token — the caller MUST adopt it (AuthContext `adoptToken`).
// ==========================================

export const MY_TWO_FACTOR_STATUS = gql`
  query MyTwoFactorStatus {
    myTwoFactorStatus {
      enabled
      enabledAt
      factorTypes
      canEnable
      reason
      sessionAal
      sessionAmr
      mfaAt
      recoveryCodesRemaining
      sessionsValidAfter
    }
  }
`;

// Factor types available for a SECOND proof in this session (already-proven types are left out).
export const STEP_UP_OPTIONS = gql`
  query StepUpOptions {
    stepUpOptions
  }
`;

export const STEP_UP_WITH_PASSWORD = gql`
  mutation StepUpWithPassword($password: String!) {
    stepUpWithPassword(password: $password) {
      token
      aal
      amr
    }
  }
`;

// JSON WebAuthn request options restricted to the caller's own credentials.
export const STEP_UP_PASSKEY_OPTIONS = gql`
  mutation StepUpPasskeyOptions {
    stepUpPasskeyOptions
  }
`;

export const STEP_UP_WITH_PASSKEY = gql`
  mutation StepUpWithPasskey($response: String!) {
    stepUpWithPasskey(response: $response) {
      token
      aal
      amr
    }
  }
`;

// channel: "email" | "sms" — sent to the caller's OWN verified address/phone.
export const SEND_STEP_UP_CODE = gql`
  mutation SendStepUpCode($channel: String!) {
    sendStepUpCode(channel: $channel)
  }
`;

export const STEP_UP_WITH_CODE = gql`
  mutation StepUpWithCode($channel: String!, $code: String!) {
    stepUpWithCode(channel: $channel, code: $code) {
      token
      aal
      amr
    }
  }
`;

export const STEP_UP_WITH_RECOVERY_CODE = gql`
  mutation StepUpWithRecoveryCode($code: String!) {
    stepUpWithRecoveryCode(code: $code) {
      token
      aal
      amr
    }
  }
`;

// Needs a fresh (10 min) aal2 session. Recovery codes are returned ONCE — never stored.
export const ENABLE_TWO_FACTOR = gql`
  mutation EnableTwoFactor {
    enableTwoFactor {
      token
      recoveryCodes
    }
  }
`;

export const DISABLE_TWO_FACTOR = gql`
  mutation DisableTwoFactor {
    disableTwoFactor {
      token
      aal
      amr
    }
  }
`;

export const REGENERATE_RECOVERY_CODES = gql`
  mutation RegenerateRecoveryCodes {
    regenerateRecoveryCodes {
      token
      recoveryCodes
    }
  }
`;
